import { Button, Flex, Skeleton, Spacer, useClipboard } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React from "react";
import { RiCheckLine, RiFileCopyLine, RiShareBoxLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { AdminPage } from "src/components/Layout/AdminPage";
import { ConditionalTooltip } from "src/components/Layout/ConditionalTooltip";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { RouterLink } from "src/components/Links/RouterLink";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import {
  NavigationBlocker,
  NavigationBlockerProvider,
} from "src/components/NavigationBlocker";
import { AdminHeading } from "src/components/Text/AdminHeading";
import { useHeadlessContentConversion } from "src/components/WysiwygEditor/lexical/hooks/useContentConversion";
import * as FSR from "src/generated/hooks/form-service/requests";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { ResizableFilterPanel } from "src/scenes/orgAdmin/forms/ResizableFilterPanel";
import * as breadcrumb from "src/services/breadcrumb";
import { validateWithZod } from "src/services/formValidations";
import * as OrgService from "src/services/orgService";
import { isNullOrUndefined } from "src/services/predicates";
import * as Url from "src/services/url";
import * as RD from "src/types/remoteData";
import { AdditionalContentCard } from "./AdditionalContentCard";
import { BannerCard } from "./BannerCard";
import { FamilyCard } from "./FamilyCard";
import { FormikSchema, FormikType, fromFormik, toFormik } from "./Formik";
import { Preview } from "./Preview";
import { StaffCard } from "./StaffCard";

export const Edit = () => {
  const toast = useAvelaToast();
  const navigate = useNavigate();
  const organization = useOrganization();
  const { remoteData: landingPageRD, update } =
    OrgService.useOrgConfig<OrgConfig.LandingPageConfig>("LandingPage");
  const { remoteData: accountLookupRD } =
    OrgService.useOrgConfig<OrgConfig.AccountLookupConfig>("AccountLookup");

  const converter = useHeadlessContentConversion({ variableMode: "expanded" });

  const homeUrl = React.useMemo(() => {
    return `${window.location.origin}${organization
      .map((org) => Url.Parent.Landing.index(org))
      .withDefault("")}`;
  }, [organization]);

  const { hasCopied, onCopy } = useClipboard(homeUrl, { timeout: 5000 });
  const isLandingPageDisabled =
    !landingPageRD.hasData() || landingPageRD.data?.disabled !== false;

  return (
    <AdminPage
      breadcrumb={
        <Breadcrumb
          items={breadcrumb.landingPage.getBreadcrumbsForEdit(organization)}
          mb={8}
        />
      }
      heading={
        <AdminHeading
          title="Landing page"
          actionButton={
            <Flex gap="2">
              <ConditionalTooltip
                showTooltip={isLandingPageDisabled}
                label="Enable and update the landing page to use this action"
                hasArrow
              >
                <Button
                  variant="outline"
                  leftIcon={hasCopied ? <RiCheckLine /> : <RiFileCopyLine />}
                  isDisabled={isLandingPageDisabled}
                  onClick={() => {
                    onCopy();
                    toast({
                      title: "URL copied to clipboard",
                      status: "success",
                    });
                  }}
                >
                  Copy URL
                </Button>
              </ConditionalTooltip>
              <ConditionalTooltip
                showTooltip={isLandingPageDisabled}
                label="Enable and update the landing page to use this action"
                hasArrow
              >
                <Button
                  {...(isLandingPageDisabled
                    ? {}
                    : {
                        as: RouterLink,
                        to: homeUrl,
                      })}
                  leftIcon={<RiShareBoxLine />}
                  isExternal
                  isDisabled={isLandingPageDisabled}
                >
                  Visit
                </Button>
              </ConditionalTooltip>
            </Flex>
          }
        />
      }
    >
      <NavigationBlockerProvider>
        {(navBlocker) => (
          <RemoteDataView
            remoteData={RD.toTuple(landingPageRD, accountLookupRD)}
            error={(error) => <GenericError error={error} />}
            loading={<Loading />}
            reloading={null}
          >
            {([landingPageConfig, accountLookup]) => (
              <Formik<FormikType>
                initialValues={toFormik(
                  landingPageConfig,
                  (html: string): string =>
                    converter.toPlainText(converter.fromHtml(html)) ?? ""
                )}
                validate={validateWithZod(FormikSchema)}
                onSubmit={async (values, formikHelpers) => {
                  if (!organization.hasData()) {
                    console.error(
                      "Missing organization on landing page settings"
                    );
                    return;
                  }

                  try {
                    // upload banner image first
                    let bannerUrl: string = values.banner.imageUrl ?? "";
                    const bannerImage = values.banner.image;
                    if (
                      values.banner.status === "enabled" &&
                      !isNullOrUndefined(bannerImage)
                    ) {
                      const resp = await FSR.generateBannerImageUploadUrl({
                        path: { organizationId: organization.data.id },
                        body: {
                          name: bannerImage.name,
                        },
                      });
                      if (isNullOrUndefined(resp.data)) {
                        throw new Error("Fail uploading banner image");
                      }

                      const response = await fetch(resp.data.data.url, {
                        method: "PUT",
                        body: bannerImage,
                        headers: {
                          "Content-Type": bannerImage.type,
                        },
                      });
                      if (!response.ok) {
                        throw new Error(
                          `Failed to upload banner image: ${response.statusText}`
                        );
                      }
                      bannerUrl = resp.data.data.imageUrl;
                    }

                    // use url as banner.imageUrl
                    const config = fromFormik(values, bannerUrl);
                    await update(config);
                    formikHelpers.resetForm({ values });
                    toast({ title: "Landing page updated", status: "success" });
                    if (organization.hasData()) {
                      await navBlocker.disable(true);
                      navigate(
                        Url.OrgAdmin.Org.settings(organization.data, "tools")
                      );
                    }
                  } catch (err) {
                    console.error(err);
                    toast.genericError();
                  }
                }}
              >
                {(formik) => {
                  return (
                    <>
                      <NavigationBlocker hasChanges={formik.dirty} />
                      <Flex
                        as={Form}
                        direction="column"
                        flexBasis="1"
                        flexGrow="1"
                      >
                        <ResizableFilterPanel
                          leftContainerProps={{ paddingRight: "0.5rem" }}
                          rightContainerProps={{ paddingLeft: "0.5rem" }}
                          leftContent={
                            <Flex direction="column" flexGrow="1" gap="4">
                              <BannerCard />
                              <FamilyCard accountLookup={accountLookup} />
                              <StaffCard />

                              <AdditionalContentCard />
                              <Spacer />
                            </Flex>
                          }
                          showRightContent
                          rightContent={
                            <Preview accountLookup={accountLookup} />
                          }
                        />

                        <Spacer />
                        <AdminFormButtons justifyContent="space-between">
                          <Button
                            variant="outline"
                            colorScheme="gray"
                            onClick={async () => {
                              if (!organization.hasData()) return;
                              navigate(
                                Url.OrgAdmin.Org.settings(
                                  organization.data,
                                  "tools"
                                )
                              );
                            }}
                          >
                            Cancel
                          </Button>
                          <Button type="submit" isLoading={formik.isSubmitting}>
                            Update
                          </Button>
                        </AdminFormButtons>
                      </Flex>
                    </>
                  );
                }}
              </Formik>
            )}
          </RemoteDataView>
        )}
      </NavigationBlockerProvider>
    </AdminPage>
  );
};

const Loading = () => {
  return (
    <Flex direction="column" gap="4">
      <Skeleton width="10rem" height="2rem" />
      <Skeleton width="100%" height="4rem" />
      <Skeleton width="100%" height="20rem" />
      <Skeleton width="100%" height="20rem" />
      <Skeleton width="100%" height="4rem" />
    </Flex>
  );
};
